"use client";

import { useEffect } from "react";

/**
 * Observes legal-page sections and toggles an `active` class on the
 * matching ToC link as each section scrolls into view.
 *
 * Supports two markup flavours shipped by the handoff:
 *
 * 1. v1 layout (privacy-policy): links are `.v1-toc a[data-tgt]` and
 *    target elements are `.v1-section` with matching `id`.
 *
 * 2. legacy layout (terms-and-conditions): links are `.toc-link` whose
 *    `href="#id"` points at `.legal-section` elements with that `id`.
 *
 * The observer auto-detects which layout is present on the page and
 * wires up appropriately. Safe to render on pages using either one.
 */
export default function LegalTocActive() {
  useEffect(() => {
    const v1Links = document.querySelectorAll<HTMLAnchorElement>(
      ".v1-toc a[data-tgt]"
    );
    const v1Sections = document.querySelectorAll<HTMLElement>(".v1-section");

    const legacyLinks = document.querySelectorAll<HTMLAnchorElement>(".toc-link");
    const legacySections = document.querySelectorAll<HTMLElement>(".legal-section");

    const entries: Array<{
      links: NodeListOf<HTMLAnchorElement>;
      sections: NodeListOf<HTMLElement>;
      resolve: (el: Element) => HTMLAnchorElement | undefined;
    }> = [];

    if (v1Links.length && v1Sections.length) {
      const map = new Map<string, HTMLAnchorElement>();
      v1Links.forEach((a) => {
        const key = a.dataset.tgt;
        if (key) map.set(key, a);
      });
      entries.push({
        links: v1Links,
        sections: v1Sections,
        resolve: (el) => map.get((el as HTMLElement).id),
      });
    }

    if (legacyLinks.length && legacySections.length) {
      const map = new Map<string, HTMLAnchorElement>();
      legacyLinks.forEach((a) => {
        const href = a.getAttribute("href") ?? "";
        if (href.startsWith("#")) map.set(href.slice(1), a);
      });
      entries.push({
        links: legacyLinks,
        sections: legacySections,
        resolve: (el) => map.get((el as HTMLElement).id),
      });
    }

    if (entries.length === 0) return;

    const observers = entries.map(({ links, sections, resolve }) => {
      const observer = new IntersectionObserver(
        (intersections) => {
          intersections.forEach((entry) => {
            if (entry.isIntersecting) {
              const activeLink = resolve(entry.target);
              if (!activeLink) return;
              links.forEach((l) => l.classList.remove("active", "is-active"));
              activeLink.classList.add("active", "is-active");
            }
          });
        },
        { rootMargin: "-96px 0px -65% 0px", threshold: 0 }
      );
      sections.forEach((s) => observer.observe(s));
      return observer;
    });

    return () => {
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  return null;
}
